import { useCallback } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { NormalFormSelectionField, NormalFormDateField } from '../molecules';
import { Button } from '../atoms';
import {
  ExpensesReportFilterInput,
  IncidentCategoryOption,
  OrderDirectionOption,
  PageSizeOption,
  RentChargeStatusOption,
} from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import {
  incidentCategoryMapper,
  orderDirectionMapper,
  pageSizeMapper,
  rentChargeStatusMapper,
} from '@/helpers';

type ExpensesFiltersProps = {
  filters: ExpensesReportFilterInput;
  onFilterChange: (newFilters: ExpensesReportFilterInput) => void;
};

const textsData = {
  en: {
    field1: 'Category',
    field2: 'Status',
    field3: 'Order',
    field4: 'Page Size',
    field5: 'Created From',
    field6: 'Created To',
    clearFilters: 'Clear Filters',
  },
  es: {
    field1: 'Categoría',
    field2: 'Estado',
    field3: 'Orden',
    field4: 'Registros por página',
    field5: 'Fecha Desde',
    field6: 'Fecha Hasta',
    clearFilters: 'Limpiar Filtros',
  },
}

const FiltersWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  margin-bottom: 2rem;
`;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md};

  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

const initialFilters: ExpensesReportFilterInput = {
  createdAtFrom: null,
  createdAtTo: null,
  categoryOption: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
};

export const ExpensesFilters = ({ filters, onFilterChange }: ExpensesFiltersProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const handleFieldChange = useCallback((field: keyof ExpensesReportFilterInput | 'chargeStatus', value: string | null) => {
    // Si es chargeStatus, lo guardamos en un campo extendido pero no lo enviamos en el filtro
    if (field === 'chargeStatus') {
      // Guardamos chargeStatus en el objeto extendido pero no lo pasamos al onFilterChange
      const extendedFilters = {
        ...filters,
        chargeStatus: value as RentChargeStatusOption | null,
      };
      // Solo pasamos los campos válidos de ExpensesReportFilterInput
      const { chargeStatus: _, ...validFilters } = extendedFilters as any;
      onFilterChange(validFilters as ExpensesReportFilterInput);
    } else {
      const newFilters: ExpensesReportFilterInput = {
        ...filters,
        [field]: value,
      };
      onFilterChange(newFilters);
    }
  }, [filters, onFilterChange]);

  const categoryOptions = Object.values(IncidentCategoryOption).map(value => ({
    name: incidentCategoryMapper(value, userLanguage),
    value: value,
  }));

  const chargeStatusOptions = Object.values(RentChargeStatusOption).map(value => ({
    name: rentChargeStatusMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      return dateObj.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleDateChange = (field: 'createdAtFrom' | 'createdAtTo', value: string | null) => {
    const newFilters: ExpensesReportFilterInput = {
      ...filters,
      [field]: value ? new Date(value).toISOString() : null,
    };
    onFilterChange(newFilters);
  };

  return (
    <FiltersWrapper>
      <Row gutterWidth={16} align="center">
        <Col md={3} sm={12}>
          <NormalFormDateField
            label={texts.field5}
            name="createdAtFrom"
            value={formatDateForInput(filters.createdAtFrom)}
            onChange={(value) => handleDateChange('createdAtFrom', value)}
          />
        </Col>
        <Col md={3} sm={12}>
          <NormalFormDateField
            label={texts.field6}
            name="createdAtTo"
            value={formatDateForInput(filters.createdAtTo)}
            onChange={(value) => handleDateChange('createdAtTo', value)}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<IncidentCategoryOption>
            label={texts.field1}
            name="category"
            options={categoryOptions}
            value={filters.categoryOption}
            onChange={(value) => handleFieldChange('categoryOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<RentChargeStatusOption>
            label={texts.field2}
            name="chargeStatus"
            options={chargeStatusOptions}
            value={(filters as any).chargeStatus}
            onChange={(value) => handleFieldChange('chargeStatus', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.field3}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </Col>
      </Row>
      <Row gutterWidth={16} align="center">
        <Col md={2} sm={6}>
          <NormalFormSelectionField<PageSizeOption>
            label={texts.field4}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
        <ButtonCol md={10} sm={12}>
          <Button secondary onClick={() => {
            // Limpiar filtros: usar solo initialFilters que no incluye chargeStatus
            onFilterChange(initialFilters);
          }}>
            {texts.clearFilters}
          </Button>
        </ButtonCol> 
      </Row>
    </FiltersWrapper>
  );
};

